import { Box, Collapsible, HStack, Icon, Separator, Spacer, Stack, Text } from "@chakra-ui/react";
import { ReactElement, useState } from "react";
import { FiArrowUpRight } from "react-icons/fi";
import { PiMinusCircleBold, PiPlusCircleBold } from "react-icons/pi";
import { NavLink } from "react-router";

import { PageNavItem, useNavbarPages } from "@/components/navbar/use-navbar-pages";
import { useRole } from "@/hooks/useRole";
import { AmplitudeTrackingEvent, trackAmplitudeEvent } from "@/lib/amplitude";

export const MobileNavbar = () => {
  const { isAdmin } = useRole();
  const { PUBLIC_PAGES, ADMIN_PAGES, FAVORITE_PAGES, MORE_PAGES } = useNavbarPages();

  return (
    <Stack direction="column" textAlign={"center"} h="full" justifyContent="space-between">
      <Stack direction="column" gap={0} pb={"120px"} w="full">
        {PUBLIC_PAGES.map((page) => {
          const { name, to, icon, showExternalIcon } = page;
          return <StyledNavLinkMobile key={name} name={name} to={to} icon={icon} showExternalIcon={showExternalIcon} />;
        })}

        {isAdmin && (
          <>
            <Separator flex={1} my={2} />
            {ADMIN_PAGES.map((page) => {
              const { name, to, icon, showExternalIcon } = page;
              return (
                <StyledNavLinkMobile key={name} name={name} to={to} icon={icon} showExternalIcon={showExternalIcon} />
              );
            })}
          </>
        )}

        <>
          <Separator flex={1} my={2} />
          {FAVORITE_PAGES.map((page) => {
            return <StyledNavLinkMobile key={page.name} {...page} />;
          })}
          {MORE_PAGES.length ? <MorePageCollapsible pages={MORE_PAGES} /> : null}
        </>
      </Stack>
    </Stack>
  );
};

const MorePageCollapsible = ({ pages }: { pages: PageNavItem[] }) => {
  const [open, setOpen] = useState(false);
  return (
    <Collapsible.Root onOpenChange={(d) => setOpen(d.open)} open={open}>
      <Collapsible.Trigger
        py={2.5}
        px={3}
        cursor="pointer"
        w="full"
        color="fg.muted"
        _hover={{
          backgroundColor: "bg.subtle", // Light gray background on hover
          color: "fg", // Darker text color on hover
        }}
        rounded="md"
        h={58}
      >
        <HStack>
          <Icon boxSize={5} mr={1.5}>
            {open ? <PiMinusCircleBold /> : <PiPlusCircleBold />}
          </Icon>
          {open ? "Less" : "More"}
        </HStack>
      </Collapsible.Trigger>
      <Collapsible.Content>
        {pages.map((p) => (
          <StyledNavLinkMobile key={p.name} {...p} />
        ))}
      </Collapsible.Content>
    </Collapsible.Root>
  );
};

export const StyledNavLinkMobile = ({
  name,
  to,
  icon,
  showExternalIcon,
  shouldShowNotification,
  styledOverrides,
  iconSize = 5,
}: {
  name: string;
  to: string;
  icon?: ReactElement;
  showExternalIcon?: boolean;
  shouldShowNotification?: boolean;
  styledOverrides?: React.CSSProperties;
  iconSize?: number;
}) => {
  const [hovered, setHovered] = useState(false);
  return (
    <Box
      _hover={{
        backgroundColor: "#F1F5F980", // Light blue background on hover
        color: "fg", // Darker text color on hover
      }}
      rounded="md"
      color="fg.muted"
      h={58}
      onMouseEnter={() => setHovered(true)}
      onMouseLeave={() => setHovered(false)}
    >
      <NavLink
        key={name}
        to={to}
        target={to.startsWith("/") ? undefined : "_blank"}
        onClick={() => {
          trackAmplitudeEvent(AmplitudeTrackingEvent.PortalClickedNav, {
            nav_item: name,
          });
        }}
        style={({ isActive }) => ({
          display: "flex",
          alignItems: "center",
          width: "100%",
          padding: "10px 12px",
          borderRadius: "8px",
          backgroundColor: isActive ? "#F1F5F9" : "transparent",
          textDecoration: "none",
          color: isActive ? "#111111" : "inherit",
          fontWeight: "400",
          gap: "13px",
          fontSize: "16px",
          lineHeight: "24px",
          height: "100%",
          letterSpacing: "-0.18px",
          ...styledOverrides,
        })}
      >
        <HStack w="full">
          <Icon boxSize={iconSize} mr={1.5}>
            {icon}
          </Icon>
          <Text>{name}</Text>
          <Spacer flex={1} />
          {hovered && showExternalIcon && <FiArrowUpRight color="#989898" style={{ marginLeft: "-7px" }} />}
          {shouldShowNotification && (
            <Box width="16px" height="16px" position="relative">
              <svg
                xmlns="http://www.w3.org/2000/svg"
                width="16"
                height="16"
                viewBox="0 0 16 16"
                fill="none"
                style={{ animation: "pulse 2s infinite" }}
              >
                <circle cx="8" cy="8" r="8" fill="#FDECDD" />
                <circle cx="8" cy="8" r="4" fill="#B95700" />
              </svg>
            </Box>
          )}
        </HStack>
      </NavLink>
    </Box>
  );
};
